import React, { useState, useEffect, useCallback } from 'react';

// ============================================
// 10% Lossback claim panel — the claim flow on the /lossback page.
// Eligibility: $10K+ weighted wager this leaderboard period, one claim per
// period, submittable only on the 17th–22nd of the month (UK time).
// Screenshot is downscaled client-side (canvas → JPEG base64).
// Admins get a preview toggle that opens the form ignoring the wager/link
// gates (backend honours { adminPreview } for admin accounts) so the flow
// can be tested without $10K wagered.
// ============================================

const fmt = (n) => '$' + Math.round(parseFloat(n || 0)).toLocaleString();

// Keep in sync with backend LOSSBACK_MAX_IMAGE_CHARS (~800KB binary as base64)
const MAX_IMAGE_CHARS = 1100000;

// Load a File into an <img> without depending on FileReader. Some iOS webviews
// / in-app browsers (Discord, Chrome-on-iOS) don't expose FileReader and throw
// "Can't find variable: FileReader" — URL.createObjectURL works there. We fall
// back to FileReader only if createObjectURL is missing.
const loadImageFromFile = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  let objectUrl = null;
  const fail = () => {
    if (objectUrl) { try { URL.revokeObjectURL(objectUrl); } catch (e) {} }
    reject(new Error("Couldn't open that image. If it's an iPhone photo (HEIC), open it and take a screenshot, or save it as PNG/JPG, then try again."));
  };
  img.onload = () => {
    if (objectUrl) { try { URL.revokeObjectURL(objectUrl); } catch (e) {} }
    resolve(img);
  };
  img.onerror = fail;

  if (typeof URL !== 'undefined' && URL.createObjectURL) {
    try {
      objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
      return;
    } catch (e) { /* fall through to FileReader */ }
  }
  if (typeof FileReader === 'undefined') {
    reject(new Error('Your browser blocked image processing here — try Safari, or upload from a computer.'));
    return;
  }
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read that file — please try a different screenshot.'));
  reader.onload = () => { img.src = reader.result; };
  reader.readAsDataURL(file);
});

// Downscale to max 1280px and re-encode as JPEG so uploads stay small. Drops
// quality until it fits the size cap, and gives actionable errors (a common
// failure is an iPhone HEIC photo that the browser can't decode).
const downscaleImage = async (file) => {
  if (!file.type || !file.type.startsWith('image/')) {
    throw new Error("That doesn't look like an image — please upload a PNG or JPG screenshot.");
  }
  const img = await loadImageFromFile(file);
  const MAX = 1280;
  const scale = Math.min(1, MAX / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  let q = 0.8;
  let out = canvas.toDataURL('image/jpeg', q);
  while (out.length > MAX_IMAGE_CHARS && q > 0.4) {
    q -= 0.15;
    out = canvas.toDataURL('image/jpeg', q);
  }
  if (out.length > MAX_IMAGE_CHARS) {
    throw new Error('That image is too large even after compression — crop it to just the stats and try again.');
  }
  return out;
};

const CLAIM_STATUS = {
  pending:  { label: '⏳ In Review', color: '#fbbf24' },
  approved: { label: '✅ Approved', color: '#22c55e' },
  denied:   { label: '❌ Denied', color: '#f87171' }
};

export default function LossbackPanel({ apiUrl, getToken }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [adminPreview, setAdminPreview] = useState(false); // admins: force the form open to test it

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/lossback/my-claim`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) setInfo(await res.json());
      else setError('Failed to load your lossback status');
    } catch (e) {
      setError('Failed to load your lossback status');
    }
    setLoading(false);
  }, [apiUrl, getToken]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      setScreenshot(await downscaleImage(file));
    } catch (err) {
      setError(err.message || 'Could not process that image');
    }
  };

  const submit = async () => {
    if (!screenshot) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/lossback/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ screenshot, ...(forceForm ? { adminPreview: true } : {}) })
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setScreenshot(null);
        load();
      } else {
        setError(body.error || 'Failed to submit claim');
      }
    } catch (e) {
      setError('Failed to submit claim');
    }
    setSubmitting(false);
  };

  const progress = info ? Math.min(100, (info.weightedWager / info.threshold) * 100) : 0;
  const st = info?.claim ? (CLAIM_STATUS[info.claim.status] || CLAIM_STATUS.pending) : null;
  const cap = info?.cap ? fmt(info.cap) : '$2,000';
  const inReview = info?.claim?.status === 'pending';
  const claimedThisPeriod = !!info?.claimedThisPeriod;
  const windowOpen = !!info?.windowOpen;
  const windowLabel = `${info?.windowStartDay || 17}th–${info?.windowEndDay || 22}nd`;
  // Lock the submit form while a claim is in review or one was already submitted this
  // leaderboard period. A denied claim falls through so the user can fix and resubmit.
  const showStatus = info && info.claim && (inReview || claimedThisPeriod);
  // Admin preview: force the upload form open (ignores wager/link/status gates) so admins can test it.
  const forceForm = !!(info?.isAdmin && adminPreview);

  return (
      <div>
        {info?.isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.75rem', marginBottom: '1rem', borderRadius: '10px', background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.3)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: '#c4b5fd' }}>
            <input type="checkbox" checked={adminPreview} onChange={(e) => setAdminPreview(e.target.checked)} style={{ accentColor: '#a855f7', width: '16px', height: '16px', cursor: 'pointer' }} />
            🛠️ Admin preview — open the claim form ignoring wager &amp; link checks
          </label>
        )}

        {loading ? (
          <p style={{ textAlign: 'center', color: '#71717a' }}>Checking your eligibility…</p>
        ) : !info ? (
          <p style={{ textAlign: 'center', color: '#fca5a5' }}>{error || 'Something went wrong.'}</p>
        ) : showStatus && !forceForm ? (
          /* In review, or a claim was already submitted this leaderboard period */
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: st.color, marginBottom: '0.6rem' }}>{st.label}</div>
            {info.claim.status === 'approved' && info.claim.payout_amount && (
              <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#22c55e', marginBottom: '0.6rem' }}>
                {fmt(info.claim.payout_amount)}
              </div>
            )}
            {info.claim.admin_response && (
              <p style={{ color: '#a1a1aa', fontSize: '0.88rem', lineHeight: 1.6, margin: '0 0 0.6rem' }}>
                <strong style={{ color: '#c4b5fd' }}>Admin:</strong> {info.claim.admin_response}
              </p>
            )}
            {inReview && (
              <p style={{ color: '#a1a1aa', fontSize: '0.82rem', lineHeight: 1.6, margin: '0 0 0.6rem' }}>
                Your claim covers the <strong style={{ color: '#c4b5fd' }}>current leaderboard period</strong> and is
                crosschecked against Shuffle's reported P&amp;L for that period before payout — not the figure on your
                screenshot. This verification takes time, so lossback pays out slower than other rewards. Thanks for
                your patience.
              </p>
            )}
            <p style={{ color: '#71717a', fontSize: '0.78rem', margin: 0 }}>
              Submitted {new Date(info.claim.created_at).toLocaleDateString('en-GB')} · period {info.periodLabel}
              <br />One claim per leaderboard period — you can submit your next claim on the {windowLabel} of next month.
            </p>
          </div>
        ) : !info.shuffleLinked && !forceForm ? (
          <p style={{ textAlign: 'center', color: '#fbbf24', fontSize: '0.92rem' }}>
            ⚠️ Link your Shuffle account first (sidebar → Link Shuffle Account) so we can verify your wager.
          </p>
        ) : (
          <div>
            {/* Eligibility progress */}
            <div style={{ marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#a1a1aa', marginBottom: '0.4rem' }}>
                <span>Weighted wager this period</span>
                <span><strong style={{ color: info.eligible ? '#22c55e' : '#fff' }}>{fmt(info.weightedWager)}</strong> / {fmt(info.threshold)}</span>
              </div>
              <div style={{ height: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progress}%`, borderRadius: '4px', background: info.eligible ? 'linear-gradient(90deg, #22c55e, #4ade80)' : 'linear-gradient(90deg, #a855f7, #fbbf24)' }} />
              </div>
            </div>

            {(info.eligible || forceForm) ? (
              <div>
                {forceForm && !info.eligible && (
                  <p style={{ color: '#c4b5fd', fontSize: '0.78rem', lineHeight: 1.5, margin: '0 0 0.9rem', padding: '0.55rem 0.7rem', borderRadius: '8px', background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.25)' }}>
                    🛠️ Admin preview. Submitting creates a real test claim (it'll show in the admin Lossback tab) — deny it there so it doesn't count as this period's claim, then test again.
                  </p>
                )}
                <p style={{ color: '#a1a1aa', fontSize: '0.85rem', lineHeight: 1.6, margin: '0 0 0.9rem' }}>
                  Your claim is for the <strong style={{ color: '#fff' }}>current leaderboard period</strong> (started on
                  the 24th). Upload a screenshot of your <strong style={{ color: '#fff' }}>Shuffle PNL statistics</strong>{' '}
                  for this period. To get your P&amp;L, either ask <strong style={{ color: '#fff' }}>Shuffle live
                  support</strong> for your statistics from the <strong style={{ color: '#fff' }}>24th of the month</strong>{' '}
                  (when the leaderboard period started), or go to <strong style={{ color: '#fff' }}>Transactions → Other →
                  Request statistics</strong> on Shuffle and it'll be sent to your email.
                  You'll get back <strong style={{ color: '#fff' }}>10% of your losses, up to {cap}</strong>. Only losses
                  on your own <strong style={{ color: '#fff' }}>deposits</strong> count — tips you receive are not included.
                </p>
                <div style={{ color: '#c4b5fd', fontSize: '0.8rem', lineHeight: 1.6, margin: '0 0 0.9rem', padding: '0.7rem 0.8rem', borderRadius: '10px', background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.22)' }}>
                  <strong style={{ color: '#fff', display: 'block', marginBottom: '0.35rem' }}>How your payout is worked out</strong>
                  The screenshot is just so we have an idea of what to expect — it doesn't lock in your payout. We crosscheck
                  every claim against the <strong style={{ color: '#fff' }}>P&amp;L Shuffle reports for the leaderboard
                  period</strong>, so being down a certain amount today doesn't mean you'll get exactly 10% of that figure.
                  Verification with Shuffle takes time, so lossback pays out slower than other rewards.
                </div>
                <label style={{ display: 'block', padding: screenshot ? '0' : '1.6rem', border: '2px dashed rgba(168,85,247,0.4)', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', color: '#c4b5fd', fontSize: '0.88rem', fontWeight: 600, marginBottom: '0.9rem', overflow: 'hidden' }}>
                  {screenshot ? (
                    <img src={screenshot} alt="PNL screenshot preview" style={{ width: '100%', display: 'block', maxHeight: '260px', objectFit: 'contain', background: '#000' }} />
                  ) : (
                    <>📷 Click to upload your PNL screenshot</>
                  )}
                  <input type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
                </label>
                {error && <p style={{ color: '#fca5a5', fontSize: '0.82rem', margin: '0 0 0.8rem' }}>{error}</p>}
                <button
                  onClick={submit}
                  disabled={!screenshot || submitting}
                  style={{ width: '100%', padding: '0.8rem', background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', border: 'none', borderRadius: '12px', color: '#1c1205', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif', opacity: !screenshot || submitting ? 0.6 : 1 }}
                >
                  {submitting ? 'Submitting…' : 'Submit Lossback Claim'}
                </button>
                <p style={{ color: '#71717a', fontSize: '0.72rem', margin: '0.7rem 0 0', textAlign: 'center' }}>
                  One claim per leaderboard period · submissions open {windowLabel} · 10% of deposit losses up to {cap} (tips excluded) · verified against Shuffle.
                  Falsified screenshots disqualify all rewards.
                </p>
              </div>
            ) : !windowOpen && info.meetsWager ? (
              <p style={{ textAlign: 'center', color: '#a1a1aa', fontSize: '0.88rem', margin: 0 }}>
                🗓️ You meet the wager requirement! Lossback claims can only be submitted on the{' '}
                <strong style={{ color: '#fbbf24' }}>{windowLabel}</strong> of the month — come back then to claim for
                the current leaderboard period.
              </p>
            ) : (
              <p style={{ textAlign: 'center', color: '#a1a1aa', fontSize: '0.88rem', margin: 0 }}>
                Keep wagering under guild SNUTZY — you'll unlock lossback at {fmt(info.threshold)} weighted this period.
                Claims are submitted on the {windowLabel} of the month.
              </p>
            )}
          </div>
        )}
      </div>
  );
}
